import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { apiRequest, getImageUrl } from '../utils/api';

/**
 * StoryViewer - Instagram benzeri tam ekran story görüntüleyici
 * STABIL VERSIYON - useRef ile callback'ler sabitlendi
 */
const StoryViewer = ({ 
    stories = [], 
    initialIndex = -1,
    onClose,
    onStoryView,
    onStoryComplete,
    organizationId,
    visitorId,
    viewedStoryIds = []
}) => {
    // Find first unviewed story index
    const startIndex = stories.findIndex(s => !viewedStoryIds.includes(s.id));
    const [currentIndex, setCurrentIndex] = useState(initialIndex >= 0 ? initialIndex : (startIndex >= 0 ? startIndex : 0));
    const [progress, setProgress] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const [isMuted, setIsMuted] = useState(true);
    
    const videoRef = useRef(null);
    const imageTimerRef = useRef(null);
    const videoTimerRef = useRef(null);
    const progressIntervalRef = useRef(null);
    const animationFrameRef = useRef(null);
    const touchStartX = useRef(null);
    const touchStartTime = useRef(null);
    const hasAdvancedRef = useRef(false);
    const storyStartTimeRef = useRef(null);
    const pausedElapsedRef = useRef(0); // Track elapsed time when paused
    
    // Store callbacks in refs to prevent re-renders
    const onCloseRef = useRef(onClose);
    const onStoryViewRef = useRef(onStoryView);
    const onStoryCompleteRef = useRef(onStoryComplete);
    const visitorIdRef = useRef(visitorId);
    const goNextRef = useRef(null);
    const goPrevRef = useRef(null);
    const clearAllTimersRef = useRef(null);
    const recordViewRef = useRef(null);
    
    // Update refs when props change
    useEffect(() => {
        onCloseRef.current = onClose;
        onStoryViewRef.current = onStoryView;
        onStoryCompleteRef.current = onStoryComplete;
        visitorIdRef.current = visitorId;
    }, [onClose, onStoryView, onStoryComplete, visitorId]);

    const currentStory = stories[currentIndex];
    const isVideo = currentStory?.media_type === 'video';
    
    // Memoize duration to prevent unnecessary re-renders
    const duration = useMemo(() => {
        return currentStory ? (currentStory.display_duration || 5) * 1000 : 5000;
    }, [currentStory?.display_duration]);

    // Clear all timers
    const clearAllTimers = useCallback(() => {
        if (imageTimerRef.current) {
            clearTimeout(imageTimerRef.current);
            imageTimerRef.current = null;
        }
        if (videoTimerRef.current) {
            clearTimeout(videoTimerRef.current);
            videoTimerRef.current = null;
        }
        if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
            progressIntervalRef.current = null;
        }
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }
    }, []);
    
    // Store clearAllTimers in ref
    clearAllTimersRef.current = clearAllTimers;

    // Record view
    const recordView = useCallback(async (storyId, completed = false) => {
        if (!visitorIdRef.current || !storyId) return;
        try {
            await apiRequest(`/menu/stories/${storyId}/view`, {
                method: 'POST',
                body: JSON.stringify({ 
                    visitorId: visitorIdRef.current, 
                    completed,
                    deviceInfo: {
                        userAgent: navigator.userAgent,
                        screenWidth: window.screen.width,
                        screenHeight: window.screen.height
                    }
                }),
                headers: { 'Content-Type': 'application/json' }
            });
            if (completed) onStoryCompleteRef.current?.(storyId);
        } catch (error) {
            console.error('Story view error:', error);
        }
    }, []);
    
    // Store recordView in ref
    recordViewRef.current = recordView;

    // Go to next story - stored in ref to prevent re-renders
    const goNext = useCallback(() => {
        if (hasAdvancedRef.current) {
            console.log('⚠️ goNext: hasAdvanced is true, skipping');
            return;
        }
        hasAdvancedRef.current = true;
        
        console.log('➡️ goNext called, currentIndex:', currentIndex, 'stories.length:', stories.length);
        
        if (clearAllTimersRef.current) {
            clearAllTimersRef.current();
        }
        
        const story = stories[currentIndex];
        if (story && recordViewRef.current) {
            recordViewRef.current(story.id, true);
            onStoryViewRef.current?.(story.id);
        }

        if (currentIndex < stories.length - 1) {
            console.log('➡️ Moving to next story, currentIndex:', currentIndex, 'nextIndex:', currentIndex + 1);
            setCurrentIndex(prev => {
                const next = prev + 1;
                console.log('➡️ setCurrentIndex called, prev:', prev, 'next:', next);
                return next;
            });
            setProgress(0);
        } else {
            console.log('➡️ Last story, closing viewer');
            onCloseRef.current?.();
        }
    }, [currentIndex, stories]); // Removed recordView
    
    // Store goNext in ref
    goNextRef.current = goNext;

    // Go to previous story - stored in ref
    const goPrev = useCallback(() => {
        if (clearAllTimersRef.current) {
            clearAllTimersRef.current();
        }
        hasAdvancedRef.current = true;
        
        if (currentIndex > 0) {
            setCurrentIndex(prev => prev - 1);
            setProgress(0);
        }
    }, [currentIndex]);
    
    // Store goPrev in ref
    goPrevRef.current = goPrev;

    // Reset state when story changes - CRITICAL FOR IMAGE STORIES
    useEffect(() => {
        console.log(`🔄 Reset effect çalıştı, currentIndex: ${currentIndex}, hasAdvanced önceki: ${hasAdvancedRef.current}`);
        
        // CRITICAL: Always reset hasAdvancedRef when story changes
        hasAdvancedRef.current = false;
        setProgress(0);
        storyStartTimeRef.current = null; // Will be set in image story effect
        pausedElapsedRef.current = 0;
        
        // Clear any running animations
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }
        
        console.log(`🔄 Reset effect tamamlandı, hasAdvanced yeni: ${hasAdvancedRef.current}`);
        
        if (currentStory && recordViewRef.current) {
            recordViewRef.current(currentStory.id, false);
        }
    }, [currentIndex]); // Removed currentStory?.id and recordView

    // Handle IMAGE stories - ULTRA SIMPLE AND STABLE
    useEffect(() => {
        // Cleanup first to prevent conflicts
        if (clearAllTimersRef.current) {
            clearAllTimersRef.current();
        }
        
        // Get current story from stories array
        const story = stories[currentIndex];
        
        // Only handle image stories
        if (!story || story.media_type === 'video') {
            return;
        }

        // Calculate duration
        const storyDuration = (story.display_duration || 5) * 1000;

        // If paused, save elapsed time and don't start timers
        if (isPaused) {
            if (storyStartTimeRef.current) {
                pausedElapsedRef.current = Date.now() - storyStartTimeRef.current;
            }
            return;
        }

        console.log(`🖼️ Image story başladı, süre: ${storyDuration/1000}s, index: ${currentIndex}`);
        
        // CRITICAL: Always reset hasAdvancedRef for new story
        hasAdvancedRef.current = false;
        
        // Set start time
        if (pausedElapsedRef.current > 0) {
            // Resume: adjust start time
            storyStartTimeRef.current = Date.now() - pausedElapsedRef.current;
            pausedElapsedRef.current = 0;
        } else {
            // New story: start from beginning
            storyStartTimeRef.current = Date.now();
        }
        
        console.log('🖼️ Story start time:', storyStartTimeRef.current);

        // Use simple setInterval - MOST RELIABLE
        let lastLogTime = Date.now();
        const intervalId = setInterval(() => {
            // Check if we should stop
            if (hasAdvancedRef.current) {
                console.log('🖼️ Progress stopped: hasAdvanced is true');
                clearInterval(intervalId);
                return;
            }
            
            if (!storyStartTimeRef.current) {
                storyStartTimeRef.current = Date.now();
            }
            
            const now = Date.now();
            const elapsed = now - storyStartTimeRef.current;
            const newProgress = Math.min((elapsed / storyDuration) * 100, 100);
            
            setProgress(newProgress);
            
            // Log every second
            if (now - lastLogTime >= 1000) {
                console.log(`🖼️ Progress: ${newProgress.toFixed(1)}% (${(elapsed/1000).toFixed(1)}s / ${(storyDuration/1000).toFixed(1)}s)`);
                lastLogTime = now;
            }
            
            // Auto-advance when progress reaches 100%
            if (newProgress >= 100) {
                console.log('🖼️ Progress reached 100%, advancing...');
                clearInterval(intervalId);
                // Call goNext first, it will set hasAdvancedRef internally
                if (goNextRef.current && !hasAdvancedRef.current) {
                    console.log('🖼️ Image story bitti, sonrakine geçiliyor');
                    goNextRef.current();
                }
            }
        }, 50); // Update every 50ms
        
        // Store interval ID in ref for cleanup
        progressIntervalRef.current = intervalId;

        // Auto-advance timer - FALLBACK
        const timeoutId = setTimeout(() => {
            if (!hasAdvancedRef.current && goNextRef.current) {
                console.log('🖼️ Image story bitti (timer fallback), sonrakine geçiliyor');
                goNextRef.current(); // goNext will set hasAdvancedRef internally
            }
        }, storyDuration + 100);
        
        imageTimerRef.current = timeoutId;

        return () => {
            console.log(`🖼️ Image story cleanup, index: ${currentIndex}`);
            clearInterval(intervalId);
            clearTimeout(timeoutId);
        };
    }, [currentIndex, isPaused, stories]); // Added stories to dependencies

    // Handle VIDEO stories - ONLY when story changes
    useEffect(() => {
        if (!currentStory || !isVideo) {
            if (clearAllTimersRef.current) {
                clearAllTimersRef.current();
            }
            return;
        }

        const video = videoRef.current;
        if (!video) {
            console.log('⚠️ Video ref bulunamadı, tekrar denenecek...');
            // Retry after a short delay
            const retryTimeout = setTimeout(() => {
                const retryVideo = videoRef.current;
                if (retryVideo) {
                    console.log('✅ Video ref bulundu, başlatılıyor...');
                    startVideoStory(retryVideo);
                }
            }, 100);
            return () => clearTimeout(retryTimeout);
        }

        const startVideoStory = (videoElement) => {
            const adminDuration = duration;
            console.log(`🎬 Video story başladı, admin süresi: ${adminDuration/1000}s`);
            
            // Reset video
            videoElement.currentTime = 0;
            videoElement.muted = isMuted || !currentStory.has_sound;
            
            // Play video
            videoElement.play().catch(err => {
                console.error('Video play error:', err);
            });
            
            // Set up progress tracking
            const updateProgress = () => {
                if (!videoElement || hasAdvancedRef.current) return;
                
                const videoProgress = (videoElement.currentTime / adminDuration) * 100;
                const clampedProgress = Math.min(videoProgress, 100);
                setProgress(clampedProgress);
                
                if (clampedProgress >= 100 && !hasAdvancedRef.current) {
                    console.log('🎬 Video süresi doldu, sonrakine geçiliyor');
                    if (goNextRef.current) {
                        goNextRef.current();
                    }
                }
            };
            
            const progressInterval = setInterval(updateProgress, 50);
            progressIntervalRef.current = progressInterval;
            
            // Fallback timeout
            const timeoutId = setTimeout(() => {
                if (!hasAdvancedRef.current && goNextRef.current) {
                    console.log('🎬 Video story bitti (timer fallback), sonrakine geçiliyor');
                    goNextRef.current();
                }
            }, adminDuration + 100);
            
            videoTimerRef.current = timeoutId;
            
            // Video ended event
            const handleVideoEnd = () => {
                if (!hasAdvancedRef.current && goNextRef.current) {
                    console.log('🎬 Video bitti (ended event), sonrakine geçiliyor');
                    goNextRef.current();
                }
            };
            
            videoElement.addEventListener('ended', handleVideoEnd);
            
            return () => {
                clearInterval(progressInterval);
                clearTimeout(timeoutId);
                videoElement.removeEventListener('ended', handleVideoEnd);
            };
        };
        
        const cleanup = startVideoStory(video);

        return () => {
            if (cleanup) cleanup();
            if (clearAllTimersRef.current) {
                clearAllTimersRef.current();
            }
        };
    }, [currentIndex, isVideo, isMuted, currentStory?.id, duration, isPaused]);

    // Pause/Resume video - SEPARATE effect
    useEffect(() => {
        if (!isVideo || !videoRef.current || hasAdvancedRef.current) return;
        
        const video = videoRef.current;
        if (isPaused) {
            video.pause();
        } else {
            video.play().catch(console.error);
        }
    }, [isPaused, isVideo]);

    // Keyboard controls
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'ArrowRight' || e.key === ' ') {
                goNextRef.current?.();
            } else if (e.key === 'ArrowLeft') {
                goPrevRef.current?.();
            } else if (e.key === 'Escape') {
                onCloseRef.current?.();
            } else if (e.key === 'm') {
                setIsMuted(prev => !prev);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []); // No dependencies - using refs

    // Touch handlers
    const handleTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
        touchStartTime.current = Date.now();
        setIsPaused(true);
    };

    const handleTouchEnd = (e) => {
        setIsPaused(false);
        if (!touchStartX.current) return;
        
        const touchEndX = e.changedTouches[0].clientX;
        const touchDuration = Date.now() - touchStartTime.current;
        const diff = touchEndX - touchStartX.current;
        const screenWidth = window.innerWidth;
        
        if (touchDuration < 200 && Math.abs(diff) < 30) {
            if (touchEndX < screenWidth / 3) goPrevRef.current?.();
            else if (touchEndX > (screenWidth * 2) / 3) goNextRef.current?.();
        } else if (Math.abs(diff) > 50) {
            if (diff > 0) goPrevRef.current?.();
            else goNextRef.current?.();
        }
        
        touchStartX.current = null;
    };

    // Handle link click
    const handleLinkClick = async () => {
        if (!currentStory?.link_url) return;
        
        try {
            await apiRequest(`/menu/stories/${currentStory.id}/click`, {
                method: 'POST',
                body: JSON.stringify({ visitorId: visitorIdRef.current }),
                headers: { 'Content-Type': 'application/json' }
            });
        } catch {}
        
        const { link_type, link_url, link_target } = currentStory;
        
        if (link_type === 'whatsapp') window.open(`https://wa.me/${link_url.replace(/\D/g, '')}`, '_blank');
        else if (link_type === 'phone') window.location.href = `tel:${link_url}`;
        else if (link_type === 'external') window.open(link_url, '_blank');
        else onCloseRef.current?.({ type: link_type, target: link_target || link_url });
    };

    // Prevent body scroll
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = ''; };
    }, []);

    if (!currentStory) {
        console.warn('⚠️ StoryViewer: currentStory is null');
        return null;
    }

    const mediaUrl = getImageUrl(currentStory.media_url);
    if (!mediaUrl) {
        console.error('❌ StoryViewer: media_url is missing or invalid', {
            storyId: currentStory.id,
            media_url: currentStory.media_url,
            media_type: currentStory.media_type
        });
    } else {
        console.log('✅ StoryViewer: Media URL:', mediaUrl, {
            storyId: currentStory.id,
            media_type: currentStory.media_type
        });
    }

    return (
        <div 
            className="fixed inset-0 z-[100] bg-black"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
        >
            {/* Progress bars */}
            <div className="absolute top-0 left-0 right-0 z-30 flex gap-1 p-2 pt-3">
                {stories.map((story, index) => (
                    <div key={story.id} className="flex-1 h-1 bg-white/30 rounded-full overflow-hidden">
                        <div 
                            className="h-full bg-white rounded-full"
                            style={{ 
                                width: `${index < currentIndex ? 100 : index === currentIndex ? progress : 0}%`,
                                transition: 'none'
                            }}
                        />
                    </div>
                ))}
            </div>

            {/* Header */}
            <div className="absolute top-6 left-0 right-0 z-30 flex items-center justify-between px-4">
                <div className="text-white text-sm font-medium">
                    {currentIndex + 1} / {stories.length}
                </div>
                
                <div className="flex items-center gap-2">
                    {isVideo && currentStory.has_sound && (
                        <button
                            onClick={() => setIsMuted(prev => !prev)}
                            className="p-2 rounded-full bg-black/30 text-white hover:bg-black/50 transition"
                        >
                            {isMuted ? (
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                                </svg>
                            ) : (
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                                </svg>
                            )}
                        </button>
                    )}
                    
                    <button
                        onClick={() => onCloseRef.current?.()}
                        className="p-2 rounded-full bg-black/30 text-white hover:bg-black/50 transition"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            </div>

            {/* Story Content */}
            <div className="absolute inset-0 flex items-center justify-center bg-black">
                {!mediaUrl ? (
                    <div className="text-white text-center p-8">
                        <p className="text-lg mb-2">⚠️ Görsel/Video bulunamadı</p>
                        <p className="text-sm opacity-75">Story ID: {currentStory.id}</p>
                    </div>
                ) : isVideo ? (
                    <video
                        ref={videoRef}
                        key={`video-${currentStory.id}-${currentIndex}`}
                        className="w-full h-full object-contain"
                        playsInline
                        muted={isMuted || !currentStory.has_sound}
                        preload="auto"
                        autoPlay
                        loop={false}
                        onError={(e) => {
                            console.error('🎬 Video yükleme hatası:', e);
                            console.error('🎬 Video URL:', mediaUrl);
                            console.error('🎬 Video element:', e.target);
                        }}
                        onLoadedData={() => {
                            console.log('🎬 Video yüklendi:', mediaUrl);
                        }}
                        onLoadStart={() => {
                            console.log('🎬 Video yüklenmeye başladı:', mediaUrl);
                        }}
                    >
                        <source src={mediaUrl} type="video/mp4" />
                        <source src={mediaUrl} type="video/quicktime" />
                        <source src={mediaUrl} />
                        Video formatı desteklenmiyor.
                    </video>
                ) : (
                    <img
                        key={`img-${currentStory.id}-${currentIndex}`}
                        src={mediaUrl}
                        alt="Story"
                        className="w-full h-full object-contain"
                        onError={(e) => {
                            console.error('🖼️ Görsel yükleme hatası:', e);
                            console.error('🖼️ Görsel URL:', mediaUrl);
                            console.error('🖼️ Image element:', e.target);
                        }}
                        onLoad={() => {
                            console.log('🖼️ Görsel yüklendi:', mediaUrl);
                        }}
                    />
                )}
            </div>

            {/* Navigation Areas */}
            <div className="absolute inset-0 flex z-10">
                <div className="w-1/3 h-full cursor-pointer" onClick={() => goPrevRef.current?.()} />
                <div className="w-1/3 h-full" />
                <div className="w-1/3 h-full cursor-pointer" onClick={() => goNextRef.current?.()} />
            </div>

            {/* Link Button - stopPropagation ile touch event'lerin story kontrolüne karışması engellenir */}
            {currentStory.link_url && (
                <div
                    className="absolute bottom-8 left-0 right-0 z-30 flex justify-center px-4"
                    onTouchStart={(e) => e.stopPropagation()}
                    onTouchEnd={(e) => e.stopPropagation()}
                    onTouchMove={(e) => e.stopPropagation()}
                >
                    <button
                        onClick={handleLinkClick}
                        className="px-6 py-3 bg-white text-black font-semibold rounded-full flex items-center gap-2 shadow-lg hover:bg-gray-100 transition active:scale-95"
                    >
                        <svg className="w-5 h-5 rotate-45" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                        </svg>
                        {currentStory.link_text || 'Daha Fazla'}
                    </button>
                </div>
            )}

            {/* Pause indicator */}
            {isPaused && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="p-4 rounded-full bg-black/30">
                        <svg className="w-12 h-12 text-white" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                        </svg>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StoryViewer;
